import { useRxCollectionPatient } from "$global-providers/rxdb-provider/collections/patient";

interface InsurerFormValue {
  type: string;
  insuranceDetails?: { insuranceNumber?: string | null } | null;
}

/**
 * On every invoice insurer save, write the primary insurer's membership
 * number back to the patient RxDB record so that
 * `useFetchPatientInsuranceRxdb` can pre-fill it on subsequent invoices.
 *
 * MVP: single-scheme — takes the first insurer that carries a non-empty
 * `insuranceDetails.insuranceNumber`.  No-ops silently when patientId is
 * absent, the collection isn't ready, or no insurer has a membership number.
 * Errors are swallowed: this is a best-effort cache write and must not break
 * the invoice insurer save flow if RxDB is transiently unavailable.
 */
export function useSavePatientInsuranceRxdb(patientId: string | undefined) {
  const collection = useRxCollectionPatient();

  const saveInsurance = async (insurers: InsurerFormValue[]) => {
    if (!collection || !patientId) return;

    const primary = insurers.find((i) => i.insuranceDetails?.insuranceNumber);
    if (!primary) return;

    const membershipNumber = primary.insuranceDetails!.insuranceNumber!;

    try {
      const doc = await collection.findOne(patientId).exec();
      if (!doc) return;

      const existingDetails = (doc.insurance?.details ?? {}) as Record<
        string,
        unknown
      >;

      // Skip the patch when nothing would actually change to avoid creating
      // unnecessary document revisions and replication churn.
      if (
        doc.insurance?.type === primary.type &&
        doc.insurance?.number === membershipNumber &&
        existingDetails.insuranceNumber === membershipNumber
      )
        return;

      await doc.incrementalPatch({
        insurance: {
          type: primary.type,
          number: membershipNumber,
          // useFetchPatientInsuranceRxdb maps ins.details → insuranceDetails,
          // so write insuranceNumber into details so the pre-fill reads it.
          details: {
            ...existingDetails,
            insuranceNumber: membershipNumber,
          },
        },
      });
    } catch {
      // Best-effort — patient cache update must not surface as an invoice save error.
    }
  };

  return { saveInsurance };
}
